/**
 * Django session login from Classic Site React (cross-origin safe).
 *
 * Uses django-allauth POST /accounts/login/ with email + password.
 * ACCOUNT_AUTHENTICATION_METHOD = email on backend.
 *
 * CSRF: primed via GET login page HTML (cookie may be HttpOnly on API host;
 * token extracted from form hidden field).
 */

import { fetchSessionProfile } from "./authApi";
import { getDjangoSiteOrigin } from "./djangoOrigin";

const CSRF_INPUT_RE =
  /name=["']csrfmiddlewaretoken["'][^>]*value=["']([^"']+)["']|value=["']([^"']+)["'][^>]*name=["']csrfmiddlewaretoken["']/i;

/**
 * @returns {Promise<{ csrfToken: string, loginUrl: string }>}
 */
export async function fetchDjangoLoginCsrf() {
  const origin = getDjangoSiteOrigin();
  if (!origin) {
    throw new Error("REACT_APP_HOODIE_API_BASE_URL is not configured");
  }

  const loginUrl = `${origin}/accounts/login/`;
  const response = await fetch(loginUrl, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/html,application/xhtml+xml" },
  });

  if (!response.ok) {
    throw new Error(`Could not load Django login page (HTTP ${response.status})`);
  }

  const html = await response.text();
  const match = html.match(CSRF_INPUT_RE);
  const csrfToken = match?.[1] || match?.[2];
  if (!csrfToken) {
    throw new Error(
      "Could not read CSRF token from Django login page. Check CORS and credentials."
    );
  }

  return { csrfToken, loginUrl };
}

/**
 * @typedef {Object} DjangoLoginResult
 * @property {boolean} ok
 * @property {string|null} error
 * @property {object|null} profile
 */

/**
 * POST credentials to django-allauth; verify session via hoodies/me/.
 * @param {{ email: string, password: string, returnUrl?: string }} params
 * @returns {Promise<DjangoLoginResult>}
 */
export async function postDjangoSessionLogin({ email, password, returnUrl }) {
  try {
    const { csrfToken, loginUrl } = await fetchDjangoLoginCsrf();
    const body = new URLSearchParams({
      csrfmiddlewaretoken: csrfToken,
      login: email.trim(),
      password,
    });
    if (returnUrl) {
      body.set("next", returnUrl);
    }

    await fetch(loginUrl, {
      method: "POST",
      credentials: "include",
      redirect: "manual",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/html,application/json",
        "X-CSRFToken": csrfToken,
      },
      body: body.toString(),
    });

    const session = await fetchSessionProfile();
    if (session.status === "authenticated") {
      return { ok: true, error: null, profile: session.profile };
    }

    return {
      ok: false,
      error:
        session.message ||
        "Login failed. Check email and password, or use Django login in a new tab.",
      profile: null,
    };
  } catch (err) {
    return {
      ok: false,
      error: err.message || "Login request failed",
      profile: null,
    };
  }
}
